import { Button } from "@/components/atoms/Buttons/Button";
import { InputField } from "@/components/molecules/InputField/InputField";
import { useEnter } from "@/hooks/useKeyboardShortcut";
import { cn } from "@/lib/utils";
import { addAlert } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import {
  TacticsResponses,
  editTactic,
  setEditTacticStatusIdle,
  setUploadTacticStatusIdle,
  uploadTactic,
} from "@/redux/tactics/tacticsSlice";
import { useEffect, useState } from "react";
import { Oval } from "react-loading-icons";
import { useDispatch, useSelector } from "react-redux";

import ActionModal from "../ActionModal";

const UploadOrEditTacticsActionModal = ({
  onDismiss,
  type,
  tacticName: tacticNameProp,
  tacticsCode: tacticsCodeProp,
  tacticsDescription: tacticsDescriptionProp,
  tacticId,
  responses,
}: {
  onDismiss: () => void;
  type: "upload" | "edit";
  tacticName?: string;
  tacticsCode?: string;
  tacticsDescription?: string;
  tacticId?: string;
  responses: TacticsResponses;
}) => {
  const [tacticsDescription, setTacticsDescription] = useState(
    tacticsDescriptionProp || "",
  );
  const [tacticName, setTacticName] = useState(tacticNameProp || "");
  const [tacticsCode, setTacticsCode] = useState(tacticsCodeProp || "");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const dispatch = useDispatch<AppDispatch>();
  useEnter(() => {
    const formEvent = { preventDefault: () => {} } as React.FormEvent;
    handleSubmit(formEvent);
  });

  const handleDismiss = () => {
    dispatch(setUploadTacticStatusIdle());
    dispatch(setEditTacticStatusIdle());
    onDismiss();
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!tacticName.trim()) {
      newErrors.tacticName = "Tactic Name is required.";
    } else if (tacticName.length > 30) {
      newErrors.tacticName = "Tactic Name cannot exceed 30 characters.";
    }

    if (!tacticsCode.trim()) {
      newErrors.tacticsCode = "Tactics Code is required.";
    } else if (tacticsCode.trim().length !== 11) {
      newErrors.tacticsCode = "Tactics Code must be exactly 11 characters.";
    }

    if (!tacticsDescription.trim()) {
      newErrors.tacticsDescription = "Description is required.";
    } else if (tacticsDescription.length > 300) {
      newErrors.tacticsDescription =
        "Description cannot exceed 300 characters.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (responses.uploadTactic.status === "fulfilled") {
      dispatch(
        addAlert({
          description: "Tactic uploaded successfully",
          type: "success",
        }),
      );
      handleDismiss();
    }
    if (responses.editTactic.status === "fulfilled") {
      dispatch(
        addAlert({
          description: "Tactic edited successfully",
          type: "success",
        }),
      );
      handleDismiss();
    }
  }, [responses.uploadTactic.status, responses.editTactic.status, dispatch]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      if (type === "upload") {
        dispatch(
          uploadTactic({
            name: tacticName,
            description: tacticsDescription,
            eaCode: tacticsCode,
          }),
        );
      } else {
        dispatch(
          editTactic({
            id: tacticId,
            description: tacticsDescription,
            name: tacticName,
            eaCode: tacticsCode,
          }),
        );
      }
    }
  };

  const handleReset = () => {
    setTacticsDescription(tacticsDescriptionProp || "");
    setTacticName(tacticNameProp || "");
    setTacticsCode(tacticsCodeProp || "");
  };

  let buttonChildren: string | React.ReactNode;
  if (
    responses.uploadTactic.status === "pending" ||
    responses.editTactic.status === "pending"
  ) {
    buttonChildren = <Oval width={20} height={20} />;
  } else {
    buttonChildren = type === "upload" ? "Upload" : "Save";
  }

  return (
    <ActionModal
      onDismiss={handleDismiss}
      title={type === "upload" ? "Upload Tactic" : "Edit Tactic"}
      size="small"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3 mt-6">
        <div className="flex flex-col gap-y-1">
          <InputField
            value={tacticName}
            onChange={(value) => {
              setTacticName(value);
              setErrors({ ...errors, tacticName: "" });
            }}
            placeholder="Insert Tactic Name"
            maxLength={30}
            error={errors.tacticName}
          />
        </div>
        <div className="flex flex-col gap-y-1">
          <InputField
            value={tacticsCode}
            onChange={(value) => {
              setTacticsCode(value);
              setErrors({ ...errors, tacticsCode: "" });
            }}
            placeholder="Insert Tactics Code"
            error={errors.tacticsCode}
            maxLength={11}
          />
        </div>
        <div className="flex flex-col gap-y-1">
          <label
            htmlFor="tacticsDescription"
            className="text-left text-gray-200 text-sm"
          >
            Describe the tactic and how to use it
          </label>
          <textarea
            id="tacticsDescription"
            className={cn(
              "text-gray-700 p-2 rounded-md h-24 text-sm bg-white/90",
              errors.tacticsDescription && "border border-red-500",
            )}
            value={tacticsDescription}
            onChange={(e) => {
              setTacticsDescription(e.target.value);
              setErrors({ ...errors, tacticsDescription: "" });
            }}
            maxLength={300}
            placeholder="Maximum 300 characters."
          />
          <div className="flex flex-row justify-between">
            {errors.tacticsDescription && (
              <span className="text-red-500 text-xs">
                {errors.tacticsDescription}
              </span>
            )}
            <div className="text-right text-sm text-gray-500 ml-auto">
              {tacticsDescription.length}/300 characters
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-y-1">
          <div className="flex flex-row justify-end gap-x-2">
            {type === "edit" && (
              <Button
                size="large"
                onClick={handleReset}
                disabled={
                  tacticsDescription.trim() ===
                    tacticsDescriptionProp?.trim() &&
                  tacticName.trim() === tacticNameProp?.trim() &&
                  tacticsCode.trim() === tacticsCodeProp?.trim()
                }
              >
                Reset
              </Button>
            )}
            <Button type="submit" variant="tertiary" size="large">
              {buttonChildren}
            </Button>
          </div>
          {(responses.uploadTactic.status === "rejected" ||
            responses.editTactic.status === "rejected") && (
            <span className="text-red-500 text-xs mx-auto">
              {responses.uploadTactic.message || responses.editTactic.message}
            </span>
          )}
        </div>
      </form>
    </ActionModal>
  );
};

export default UploadOrEditTacticsActionModal;
